// Client-side acquisition of OIDC id tokens from Google and Microsoft.
// The id token is sent to the backend, which verifies it and issues our own
// JWTs. Used by both social login (Login.vue) and account linking (SocialLinks.vue).
//
// On the web, Google uses Google Identity Services (rendered button). Inside
// the Capacitor/Android app, WebView OAuth is blocked by Google, so we go
// through the native @capgo/capacitor-social-login plugin instead — it returns
// an id token with the SAME audience (the web client id), so backend
// verification is identical.
import { PublicClientApplication } from '@azure/msal-browser'
import defaults from '/src/utils/defaults'

const GOOGLE_CLIENT_ID = defaults.GOOGLE_CLIENT_ID
const MICROSOFT_CLIENT_ID = defaults.MICROSOFT_CLIENT_ID
// 'common' authority: work/school AND personal Microsoft accounts.
const MICROSOFT_AUTHORITY = 'https://login.microsoftonline.com/common'

export const googleConfigured = !!GOOGLE_CLIENT_ID
export const microsoftConfigured = !!MICROSOFT_CLIENT_ID


// ---- Google via native plugin (Capacitor/Android) ----
let socialLoginReady = null

async function getSocialLogin() {
    if (!socialLoginReady) {
        socialLoginReady = import('@capgo/capacitor-social-login').then(async ({ SocialLogin }) => {
            await SocialLogin.initialize({ google: { webClientId: GOOGLE_CLIENT_ID } })
            return SocialLogin
        })
    }
    return socialLoginReady
}

// Opens the native Google account picker and returns the id token.
// Throws if the user cancels (callers should ignore cancellations).
// NOTE: no custom scopes — the default Credential Manager flow already returns
// an id token carrying email + basic profile, and custom scopes would require
// modifying MainActivity (plugin limitation on Android).
export async function getGoogleIdTokenNative() {
    console.log('[oauth] native google login v2 (no scopes)')
    if (!googleConfigured) throw new Error('Google login not configured')
    const SocialLogin = await getSocialLogin()
    const res = await SocialLogin.login({
        provider: 'google',
        options: {},
    })
    const idToken = res?.result?.idToken
    if (!idToken) throw new Error('No id token returned')
    return idToken
}


// ---- Google Identity Services ----
let gisScript = null

function loadGis() {
    if (gisScript) return gisScript
    gisScript = new Promise((resolve, reject) => {
        if (window.google?.accounts?.id) return resolve()
        const script = document.createElement('script')
        script.src = 'https://accounts.google.com/gsi/client'
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'))
        document.head.appendChild(script)
    })
    return gisScript
}

// Renders the official Google button into `el`. `onToken` receives the id token
// (the GIS `credential`). GIS only issues id tokens through its OWN rendered
// button, so to use our own styled button we render this one hidden and forward
// clicks to it (see clickRenderedGoogleButton).
export async function mountGoogleButton(el, onToken, options = {}) {
    if (!googleConfigured) throw new Error('Google login not configured')
    await loadGis()
    window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (response) => {
            if (response?.credential) onToken(response.credential)
        },
    })
    window.google.accounts.id.renderButton(el, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        width: options.width || 240,
    })
}

// Forwards a click to the hidden GIS button rendered inside `hostEl`, so our own
// styled button can drive the Google id-token flow. Returns false if the GIS
// button isn't there yet (caller can surface an error).
export function clickRenderedGoogleButton(hostEl) {
    const btn = hostEl?.querySelector('[role="button"]')
    if (btn) {
        btn.click()
        return true
    }
    return false
}


// ---- Microsoft (MSAL) ----
// We use the REDIRECT flow, not popup: popups depend on window.opener surviving
// the round-trip to Microsoft, which browsers/COOP break (the app boots inside
// the popup → infinite login windows). Redirect navigates the page to Microsoft
// and back; the response is picked up at app startup (consumeMicrosoftRedirect).
let msalInstance = null
let _msResult = null    // { mode, ok, error } after redirect completion
const MS_MODE_KEY = 'ms_redirect_mode'

async function getMsal() {
    if (!microsoftConfigured) throw new Error('Microsoft login not configured')
    if (!msalInstance) {
        msalInstance = new PublicClientApplication({
            auth: {
                clientId: MICROSOFT_CLIENT_ID,
                authority: MICROSOFT_AUTHORITY,
                // Land back INSIDE the app's router base ('/app/'), not at the
                // origin root: returning to '/' forces a cross-base hard reload
                // to '/app/…' that wipes the in-memory redirect result before we
                // can notify the user. Landing at '/app/' keeps it a single load.
                redirectUri: window.location.origin + '/app/',
                // We control routing after the redirect ourselves.
                navigateToLoginRequestUrl: false,
            },
            cache: { cacheLocation: 'sessionStorage' },
        })
        await msalInstance.initialize()
    }
    return msalInstance
}

// Eagerly initialize MSAL (call on page load) so the click handler runs fast.
export async function warmupMicrosoft() {
    if (!microsoftConfigured) return
    await getMsal()
}

// Starts Microsoft sign-in by navigating the whole page to Microsoft. `mode` is
// 'login' or 'link'; it's remembered across the redirect. Control returns to the
// app at redirectUri; see consumeMicrosoftRedirect.
export async function startMicrosoftRedirect(mode = 'login') {
    sessionStorage.setItem(MS_MODE_KEY, mode)
    const msal = await getMsal()
    await msal.loginRedirect({ scopes: ['openid', 'email', 'profile'] })
}

// MUST run once at app startup (main.js), BEFORE the app mounts. If we're
// returning from a Microsoft redirect, this completes the WHOLE flow here:
// it verifies the MSAL response, then exchanges the id token with our backend
// (login → stores our JWTs in localStorage so the app boots authenticated;
// link → posts the social identity). Doing it pre-mount makes it immune to the
// router's post-redirect navigation/reload wiping in-memory state.
export async function consumeMicrosoftRedirect() {
    if (!microsoftConfigured) return
    const mode = sessionStorage.getItem(MS_MODE_KEY) || 'login'
    let idToken = null
    try {
        const msal = await getMsal()
        const result = await msal.handleRedirectPromise()
        if (!result?.idToken) return   // not returning from a redirect
        idToken = result.idToken
    } catch (e) {
        console.log('MSAL redirect error:', e)
        _msResult = { mode, ok: false, error: e?.errorCode || e?.message || 'msal_error' }
        sessionStorage.removeItem(MS_MODE_KEY)
        return
    }
    sessionStorage.removeItem(MS_MODE_KEY)

    const API = defaults.ROOT_API
    try {
        if (mode === 'link') {
            const res = await fetch(API + 'my_user/social/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('access_token') || '') },
                body: JSON.stringify({ provider: 'microsoft', id_token: idToken }),
            })
            if (!res.ok) {
                const d = await res.json().catch(() => ({}))
                _msResult = { mode, ok: false, error: d.error_code || 'link_failed' }
                return
            }
            _msResult = { mode, ok: true }
        } else {
            const res = await fetch(API + 'my_user/token/oauth/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider: 'microsoft', id_token: idToken }),
            })
            const d = await res.json().catch(() => ({}))
            if (!res.ok || !d.access) {
                _msResult = { mode, ok: false, error: d.error_code || 'login_failed' }
                return
            }
            localStorage.setItem('access_token', d.access)
            localStorage.setItem('refresh_token', d.refresh)
            _msResult = { mode, ok: true }
        }
    } catch (e) {
        console.log('Microsoft backend exchange error:', e)
        _msResult = { mode, ok: false, error: 'network' }
    }
}

// One-shot read of the completed Microsoft flow: { mode, ok, error } | null.
export function takeMicrosoftResult() {
    const r = _msResult
    _msResult = null
    return r
}
